import { cloudinaryImageUrl, IMAGE_WIDTH } from "../../../utils/cloudinaryDelivery";
import {
  createSanitizedMarkup,
  SERVICE_DESCRIPTION_ALLOWED_TAGS,
} from "../../../utils/sanitizeHtml";

// The Intro section of a Custom booking page: the pro's own words and up to 12
// images, wherever they put it in their list. The images keep their own shape
// (they're often designed graphics with text in them), so nothing is cropped.

const escapeText = (value) =>
  value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

// Text saved before the rich-text editor is plain; give it paragraphs.
const toIntroHtml = (body) => {
  if (/<\/?(p|br|strong|em|u|s|ul|ol|li|a|span|div|blockquote|h[1-6])\b[^>]*>/i.test(body)) {
    return body;
  }
  return body
    .split(/\n\s*\n/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)
    .map((paragraph) => `<p>${escapeText(paragraph).replace(/\n/g, "<br>")}</p>`)
    .join("");
};

const hasWords = (html) =>
  html.replace(/<[^>]*>/g, " ").replace(/&nbsp;/gi, " ").trim() !== "";

export const normalizeIntroImages = (value, { businessName = "" } = {}) =>
  (Array.isArray(value) ? value : [])
    .map((item, index) => {
      const url = typeof item?.url === "string" ? item.url.trim() : "";
      if (!url) return null;
      const alt =
        typeof item?.alt === "string" && item.alt.trim()
          ? item.alt.trim()
          : businessName
            ? `Photo ${index + 1} from ${businessName}`
            : `Photo ${index + 1}`;
      return {
        id:
          (typeof item?.id === "string" && item.id.trim()) ||
          (typeof item?._id === "string" && item._id.trim()) ||
          `intro-${index}`,
        url,
        alt,
      };
    })
    .filter(Boolean);

// An Intro with nothing in it draws nothing; one with only images has no
// heading and no empty space above them.
const ProfileIntroSection = ({ content, images = [], businessName = "" }) => {
  const heading = typeof content?.heading === "string" ? content.heading.trim() : "";
  const rawBody = typeof content?.body === "string" ? content.body.trim() : "";
  const bodyHtml = rawBody
    ? createSanitizedMarkup(toIntroHtml(rawBody), {
        allowedTags: SERVICE_DESCRIPTION_ALLOWED_TAGS,
      })
    : null;
  const hasBody = Boolean(bodyHtml && hasWords(bodyHtml.__html));
  const hasText = Boolean(heading) || hasBody;

  if (!hasText && images.length === 0) return null;

  // With text and a single image, a laptop puts the image beside the text.
  const single = hasText && images.length === 1;
  const className = [
    "profile-intro",
    hasText ? "profile-intro--with-text" : "",
    single ? "profile-intro--single" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section
      className={className}
      aria-label={heading ? undefined : businessName ? `About ${businessName}` : "About"}
    >
      <div className="profile-intro__layout">
        {hasText && (
          <div className="profile-intro__text">
            {heading && <h2 className="profile-intro__heading">{heading}</h2>}
            {hasBody && (
              <div className="profile-intro__body" dangerouslySetInnerHTML={bodyHtml} />
            )}
          </div>
        )}
        {images.length > 0 && (
          <div className="profile-intro__images">
            {images.map((image) => (
              <img
                key={image.id}
                className="profile-intro__image"
                src={cloudinaryImageUrl(image.url, { width: IMAGE_WIDTH.gallery })}
                alt={image.alt}
                loading="lazy"
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProfileIntroSection;
